import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { healthApi } from '../../../api/healthApi.js';
import { AppLayout } from '../../app/appLayout.js';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './viewHealthScreen.css';

// Map a 1-10 sleep quality onto a red(worst)->green(best) gradient. We drive an
// SVG crescent's fill with this because emoji glyphs ignore CSS color, so a
// 🌙 emoji could never show the quality gradient the feature calls for.
const colorForSleepQuality = (quality) => {
	const q = Math.max(1, Math.min(10, Number(quality) || 1));
	// hue 0 (red) at quality 1 -> hue 120 (green) at quality 10
	const hue = ((q - 1) / 9) * 120;
	return `hsl(${Math.round(hue)}, 75%, 42%)`;
};

// Issue severity color ramp. Issues are never "good", so we avoid green (which
// reads as positive) and blue (reserved for medications): mild issues are amber,
// escalating through orange to red.
const colorForIssueSeverity = (sev) => {
	if (sev == null) return '#111827';
	if (sev <= 3) return '#64748b'; // slate — minor (cool/neutral, off the warm ramp; not green or med-blue)
	if (sev <= 7) return '#ea580c'; // orange — moderate
	return '#dc2626'; // red — severe
};

// Crescent moon, filled so it can carry the quality color (feather "moon" path).
const SleepMoon = ({ color, title, onClick }) => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		className="wr-day-icon wr-sleep-moon"
		role="button"
		aria-label={title}
		onClick={onClick}
	>
		<title>{title}</title>
		<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" fill={color} />
	</svg>
);

const ViewHealthScreen = () => {
	const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 640 : false));
	const [activeMonth, setActiveMonth] = useState(() => {
		const now = new Date();
		return new Date(now.getFullYear(), now.getMonth(), 1);
	});
	const [selectedDate, setSelectedDate] = useState(() => {
		const now = new Date();
		return new Date(now.getFullYear(), now.getMonth(), now.getDate());
	});
	const [rows, setRows] = useState([]);
	const [issueRows, setIssueRows] = useState([]);
	const [exerciseRows, setExerciseRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [showIssueModal, setShowIssueModal] = useState(false);
	const [showExerciseModal, setShowExerciseModal] = useState(false);
	const [showBloodPressureModal, setShowBloodPressureModal] = useState(false);
	const [showSleepModal, setShowSleepModal] = useState(false);
	const [showEditChooser, setShowEditChooser] = useState(false);
	const [newName, setNewName] = useState('');
	const [newTakenAt, setNewTakenAt] = useState(() => {
		const now = new Date();
		return new Date(now.getTime() - now.getTimezoneOffset()*60000).toISOString().slice(0,16);
	});
	const [newIssue, setNewIssue] = useState('');
	const [isNewIssue, setIsNewIssue] = useState(false);
	const [selectedIssue, setSelectedIssue] = useState('');
	const [issueOptions, setIssueOptions] = useState([]);
	const [newDescription, setNewDescription] = useState('');
	const [newSeverity, setNewSeverity] = useState(1);
	const [newStartedAt, setNewStartedAt] = useState(() => {
		const now = new Date();
		return new Date(now.getTime() - now.getTimezoneOffset()*60000).toISOString().slice(0,16);
	});
	const [newResolvedAt, setNewResolvedAt] = useState('');
	const [bloodPressureRows, setBloodPressureRows] = useState([]);
	
	// Exercise form state
	const [exName, setExName] = useState('cardio');
	const [exDoneAt, setExDoneAt] = useState(() => {
		const now = new Date();
		const y = now.getFullYear();
		const m = String(now.getMonth()+1).padStart(2,'0');
		const d = String(now.getDate()).padStart(2,'0');
		return `${y}-${m}-${d}`;
	});
	const [exDescription, setExDescription] = useState('');
	
	// Blood pressure form state
	const [bpSystolic, setBpSystolic] = useState('');
	const [bpDiastolic, setBpDiastolic] = useState('');
	const [bpCreatedAt, setBpCreatedAt] = useState(() => {
		const now = new Date();
		const y = now.getFullYear();
		const m = String(now.getMonth()+1).padStart(2,'0');
		const d = String(now.getDate()).padStart(2,'0');
		return `${y}-${m}-${d}`;
	});

	// Sleep form state
	const [sleepRows, setSleepRows] = useState([]);
	const [slNightOf, setSlNightOf] = useState(() => {
		const now = new Date();
		const y = now.getFullYear();
		const m = String(now.getMonth()+1).padStart(2,'0');
		const d = String(now.getDate()).padStart(2,'0');
		return `${y}-${m}-${d}`;
	});
	const [slQuality, setSlQuality] = useState(8);
	const [slMagnesium, setSlMagnesium] = useState(false);
	const [slNotes, setSlNotes] = useState('');

	const copyToClipboard = useCallback(async (text) => {
		try {
			if (!text) return;
			if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
				await navigator.clipboard.writeText(text);
			} else {
				const ta = document.createElement('textarea');
				ta.value = text;
				ta.style.position = 'fixed';
				ta.style.left = '-1000px';
				document.body.appendChild(ta);
				ta.select();
				document.execCommand('copy');
				document.body.removeChild(ta);
			}
		} catch (e) {
			console.error('Failed to copy to clipboard', e);
		}
	}, []);

	const [editingType, setEditingType] = useState(null); // 'issue' | 'exercise' | null
	const [editingId, setEditingId] = useState(null);

	// Local (browser timezone) calendar-day key, e.g. "2026-07-01".
	// Using toISOString() here would convert to UTC first, which can shift
	// the date across the day boundary depending on the user's timezone
	// offset and the time-of-day on the record — causing lookups (mobile
	// single-day view keyed off selectedDate) to miss entries that the
	// desktop calendar tiles (keyed off their own local-midnight date)
	// still line up with by coincidence.
	const toLocalYMD = (d) => {
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const da = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${da}`;
	};

	const monthRange = useMemo(() => {
		const start = new Date(activeMonth.getFullYear(), activeMonth.getMonth(), 1);
		const end = new Date(activeMonth.getFullYear(), activeMonth.getMonth() + 1, 0);
		return { startDate: toLocalYMD(start), endDate: toLocalYMD(end) };
	}, [activeMonth]);

	const fetchRows = useCallback(async () => {
		setLoading(true);
		try {
			const [medsResp, issuesResp, exResp, bpResp, sleepResp] = await Promise.all([
				healthApi.getMedications(monthRange),
				healthApi.getIssues(monthRange),
				healthApi.getExercises(monthRange),
				healthApi.getBloodPressure(monthRange),
				healthApi.getSleep(monthRange),
			]);
			if (medsResp.status && medsResp.data && Array.isArray(medsResp.data.rows)) {
				setRows(medsResp.data.rows);
			} else {
				setRows([]);
			}
			if (issuesResp.status && issuesResp.data && Array.isArray(issuesResp.data.rows)) {
				setIssueRows(issuesResp.data.rows);
			} else {
				setIssueRows([]);
			}
			if (exResp && exResp.status && exResp.data && Array.isArray(exResp.data.rows)) {
				setExerciseRows(exResp.data.rows);
			} else {
				setExerciseRows([]);
			}
			if (bpResp && bpResp.status && bpResp.data && Array.isArray(bpResp.data.rows)) {
				setBloodPressureRows(bpResp.data.rows);
			} else {
				setBloodPressureRows([]);
			}
			if (sleepResp && sleepResp.status && sleepResp.data && Array.isArray(sleepResp.data.rows)) {
				setSleepRows(sleepResp.data.rows);
			} else {
				setSleepRows([]);
			}
		} catch (e) {
			console.error('Error fetching health data:', e);
			setRows([]);
			setIssueRows([]);
			setExerciseRows([]);
			setBloodPressureRows([]);
			setSleepRows([]);
		} finally {
			setLoading(false);
		}
	}, [monthRange]);

	useEffect(() => {
		fetchRows();
	}, [fetchRows]);

	// Keep activeMonth (which drives what month's data gets fetched) in sync
	// with selectedDate. The desktop Calendar updates activeMonth itself via
	// onActiveStartDateChange, but mobile's goPrevDay/goNextDay only move
	// selectedDate — without this, paging into a new month on mobile never
	// triggers a fetch for that month, so its data (e.g. issues) never loads.
	useEffect(() => {
		setActiveMonth((prev) => {
			if (prev.getFullYear() === selectedDate.getFullYear() && prev.getMonth() === selectedDate.getMonth()) {
				return prev;
			}
			return new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
		});
	}, [selectedDate]);

	// Track mobile/desktop responsive breakpoint
	useEffect(() => {
		const onResize = () => setIsMobile(window.innerWidth <= 640);
		onResize();
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	const toDateFromAny = (value) => {
		if (!value && value !== 0) return null;
		if (typeof value === 'number') {
			const ms = value < 1e12 ? value * 1000 : value;
			const d = new Date(ms);
			return isNaN(d.getTime()) ? null : d;
		}
		if (typeof value === 'object' && value.date) {
			let str = String(value.date);
			str = str.replace(' ', 'T');
			const dotIdx = str.indexOf('.');
			if (dotIdx !== -1) {
				str = str.substring(0, dotIdx);
			}
			if (value.timezone === 'UTC' && !str.endsWith('Z')) {
				str += 'Z';
			}
			const d = new Date(str);
			return isNaN(d.getTime()) ? null : d;
		}
		if (typeof value === 'string') {
			let str = value;
			if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(str)) {
				str = str.replace(' ', 'T');
				const dotIdx = str.indexOf('.');
				if (dotIdx !== -1) {
					str = str.substring(0, dotIdx);
				}
			}
			const d = new Date(str);
			if (!isNaN(d.getTime())) return d;
		}
		return null;
	};

	// Parse a date-only value (doneAt/createdAt come back as UTC midnight, e.g.
	// {"date":"2026-07-16 00:00:00.000000","timezone":"UTC"}) by reading just the
	// YYYY-MM-DD portion and building it as a local calendar date. These fields
	// represent a day, not a moment in time, so running them through toDateFromAny's
	// UTC->local conversion can roll the day backward for any timezone behind UTC.
	const toDateOnly = (value) => {
		if (!value && value !== 0) return null;
		let str = null;
		if (typeof value === 'object' && value.date) {
			str = String(value.date);
		} else if (typeof value === 'string') {
			str = value;
		}
		if (!str) return null;
		const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
		if (!match) return null;
		const [, y, m, d] = match;
		return new Date(Number(y), Number(m) - 1, Number(d));
	};

		const { medsTextByDate, issuesByDate, countsByName, issueCountsByName, exercisesByDate, rawIssuesByDate, rawExercisesByDate, bloodPressureByDate, rawBloodPressureByDate, sleepByDate } = useMemo(() => {
		const medsMap = {};
		const issuesMap = {};
		const counts = {};
		const issueCounts = {};
		const exercisesMap = {};
		const rawIssuesMap = {};
		const rawExercisesMap = {};
		const bloodPressureMap = {};
		const rawBloodPressureMap = {};
		const sleepMap = {};
		for (const r of rows) {
			const ts = r.takenAt || r.taken_at || r.date || r.takenTS;
			const d = toDateFromAny(ts);
			if (!d) continue;
			const key = toLocalYMD(d);
			const label = r.name || r.medication || r.title || 'Taken';
			medsMap[key] = medsMap[key] ? (medsMap[key] + '\n' + label) : label;
			counts[label] = (counts[label] || 0) + 1;
		}
		for (const r of issueRows) {
			const ts = r.startedAt || r.started_at || r.date || r.startedTS;
			const d = toDateFromAny(ts);
			if (!d) continue;
			const key = toLocalYMD(d);
			const label = r.issue || r.name || r.title || 'Issue';
			const severity = typeof r.severity === 'number' ? r.severity : parseInt(r.severity, 10);
			const startedAt = r.startedAt || r.started_at || null;
			const resolvedAt = r.resolvedAt || r.resolved_at || null;
			const description = r.description || '';
			if (!issuesMap[key]) issuesMap[key] = [];
			issuesMap[key].push({ label, severity: isNaN(severity) ? null : severity, startedAt, resolvedAt, description });
			if (!rawIssuesMap[key]) rawIssuesMap[key] = [];
			rawIssuesMap[key].push(r);
			issueCounts[label] = (issueCounts[label] || 0) + 1;
		}
		for (const r of exerciseRows) {
			const ts = r.doneAt || r.done_at || r.date || r.doneTS;
			const d = toDateOnly(ts);
			if (!d) continue;
			const key = toLocalYMD(d);
			const name = (r.name || '').toLowerCase();
			const description = r.description || '';
			if (!exercisesMap[key]) exercisesMap[key] = [];
			exercisesMap[key].push({ name, description });
			if (!rawExercisesMap[key]) rawExercisesMap[key] = [];
			rawExercisesMap[key].push(r);
		}
		for (const r of bloodPressureRows) {
			const ts = r.createdAt || r.created_at || r.doneAt || r.done_at || r.date || r.createdTS;
			const d = toDateOnly(ts);
			if (!d) continue;
			const key = toLocalYMD(d);
			const systolic = r.systolic || r.systolic_mm_hg || '';
			const diastolic = r.diastolic || r.diastolic_mm_hg || '';
			if (!bloodPressureMap[key]) bloodPressureMap[key] = [];
			bloodPressureMap[key].push({ systolic, diastolic });
			if (!rawBloodPressureMap[key]) rawBloodPressureMap[key] = [];
			rawBloodPressureMap[key].push(r);
		}
		for (const r of sleepRows) {
			const ts = r.nightOf || r.night_of || r.date;
			const d = toDateOnly(ts);
			if (!d) continue;
			const key = toLocalYMD(d);
			const quality = typeof r.quality === 'number' ? r.quality : parseInt(r.quality, 10);
			const magnesiumTaken = r.magnesiumTaken === true || r.magnesium_taken === true || r.magnesiumTaken === 't' || r.magnesium_taken === 't';
			const notes = r.notes || '';
			// night_of is UNIQUE, so at most one sleep record per day.
			sleepMap[key] = { quality: isNaN(quality) ? null : quality, magnesiumTaken, notes };
		}
		return { medsTextByDate: medsMap, issuesByDate: issuesMap, countsByName: counts, issueCountsByName: issueCounts, exercisesByDate: exercisesMap, rawIssuesByDate: rawIssuesMap, rawExercisesByDate: rawExercisesMap, bloodPressureByDate: bloodPressureMap, rawBloodPressureByDate: rawBloodPressureMap, sleepByDate: sleepMap };
	}, [rows, issueRows, exerciseRows, bloodPressureRows, sleepRows]);

	// Open the sleep modal for a given YYYY-MM-DD, preloading any existing record
	// for that night so the same modal serves both add and edit (create() upserts
	// on the UNIQUE night_of).
	const openSleepModal = useCallback((key) => {
		setSlNightOf(key);
		const existing = sleepByDate[key];
		if (existing) {
			setSlQuality(existing.quality || 8);
			setSlMagnesium(!!existing.magnesiumTaken);
			setSlNotes(existing.notes || '');
		} else {
			setSlQuality(8);
			setSlMagnesium(false);
			setSlNotes('');
		}
		setShowSleepModal(true);
	}, [sleepByDate]);

	const tileContent = ({ date, view }) => {
		if (view !== 'month') return null;
		const key = toLocalYMD(date);
		const medsText = medsTextByDate[key];
		const issuesForDay = issuesByDate[key] || [];
		const exForDay = exercisesByDate[key] || [];
		const bpForDay = bloodPressureByDate[key] || [];
		const sleepForDay = sleepByDate[key] || null;
		const colorForSeverity = colorForIssueSeverity;
		const fmtTime = (v) => {
			if (!v) return '';
			const d = toDateFromAny(v);
			if (!d) return '';
			return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
		};
		// Sleep is intentionally excluded here: the pencil opens the issue/exercise
		// edit chooser. Sleep is edited via the "+ Add Sleep" button, which preloads
		// the selected day's existing record and upserts it.
		const hasAny = (issuesForDay.length > 0) || (exForDay.length > 0) || (bpForDay.length > 0);
		return (
			<>
				{hasAny && (
					<button
						onClick={(e) => { e.stopPropagation(); setSelectedDate(date); setShowEditChooser(true); }}
						title="Edit issue or exercise"
						className="wr-day-edit-btn"
					>
						✏️
					</button>
				)}
				{(exForDay.length > 0 || bpForDay.length > 0 || sleepForDay) && (
					<div className="wr-day-icons">
						{sleepForDay && (
							<SleepMoon
								color={colorForSleepQuality(sleepForDay.quality)}
								title={`Sleep quality: ${sleepForDay.quality}/10${sleepForDay.magnesiumTaken ? ' • magnesium taken' : ''}${sleepForDay.notes ? `\n${sleepForDay.notes}` : ''}\n(click to edit)`}
								onClick={(e) => { e.stopPropagation(); setSelectedDate(date); openSleepModal(key); }}
							/>
						)}
						{exForDay.map((ex, idx) => {
							let icon = null;
							if (ex.name === 'cardio') icon = '❤️';
							else if (ex.name === 'upper_body') icon = '💪';
							else if (ex.name === 'tennis') icon = '🎾';
							return (
								<span
									key={`ex-${idx}`}
									className="wr-day-icon"
									title={ex.description || ex.name}
									onClick={(e) => { e.stopPropagation(); copyToClipboard(ex.description || ex.name); }}
									role="button"
									aria-label={`Copy exercise ${ex.name} description`}
								>
									{icon || '🏋️'}
								</span>
							);
						})}
						{bpForDay.map((bp, idx) => {
							const tip = `Blood Pressure: ${bp.systolic}/${bp.diastolic} mmHg`;
							return (
								<span
									key={`bp-${idx}`}
									className="wr-day-icon"
									title={tip}
									onClick={(e) => { e.stopPropagation(); copyToClipboard(tip); }}
									role="button"
									aria-label={`Copy blood pressure ${bp.systolic}/${bp.diastolic}`}
								>
									🩺
								</span>
							);
						})}
					</div>
				)}
				{medsText && (
					<div className="wr-day-content" style={{ color: 'blue' }}>{medsText}</div>
				)}
				{issuesForDay.length > 0 && (
					<div className="wr-day-content">
						{issuesForDay.map((iss, idx) => {
							const tip = `${iss.label}\n${fmtTime(iss.startedAt)} - ${fmtTime(iss.resolvedAt)}${iss.description ? `\n${iss.description}` : ''}`;
							return (
								<div key={idx} style={{ color: colorForSeverity(iss.severity), fontWeight: iss.severity != null && iss.severity >= 8 ? 700 : 500 }} title={tip}>
									{iss.label}
									{iss.severity != null && (
										<span style={{ fontWeight:700, opacity:0.85 }}> ·{iss.severity}</span>
									)}
								</div>
							);
						})}
					</div>
				)}
			</>
		);
	};

	const onActiveStartDateChange = ({ activeStartDate, view }) => {
		if (view === 'month' && activeStartDate) {
			setActiveMonth(new Date(activeStartDate.getFullYear(), activeStartDate.getMonth(), 1));
		}
	};

	const onSelectDate = (value) => {
		if (Array.isArray(value)) {
			setSelectedDate(value[0] || new Date());
		} else if (value instanceof Date) {
			setSelectedDate(value);
		}
	};

	const goPrevDay = () => {
		setSelectedDate((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1));
	};

	const goNextDay = () => {
		setSelectedDate((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1));
	};

	const submitNewMedication = async (e) => {
		e.preventDefault();
		try {
			const iso = newTakenAt;
			const resp = await healthApi.createMedication({ name: newName, takenAt: iso });
			if (!resp.status) {
				console.error('Failed to create medication', resp.msg);
				return;
			}
			setShowModal(false);
			setNewName('');
			// Reset Taken At to the currently selected date at 09:00 local
			{
				const pad = (n) => String(n).padStart(2,'0');
				const makeLocal = (d, h, m) => {
					const y = d.getFullYear();
					const mo = pad(d.getMonth()+1);
					const da = pad(d.getDate());
					const hh = pad(h);
					const mm = pad(m);
					return `${y}-${mo}-${da}T${hh}:${mm}`;
				};
				const base = selectedDate || new Date();
				setNewTakenAt(makeLocal(base, 9, 0));
			}
			fetchRows();
		} catch (err) {
			console.error('Create medication error', err);
		}
	};

	const submitNewIssue = async (e) => {
		e.preventDefault();
		try {
			const payload = {
				issue: isNewIssue ? newIssue : selectedIssue,
				description: newDescription,
				severity: Number(newSeverity),
				startedAt: newStartedAt,
				resolvedAt: newResolvedAt || undefined,
			};
			const resp = (editingType === 'issue' && editingId)
				? await healthApi.updateIssue({ id: editingId, ...payload })
				: await healthApi.createIssue(payload);
			if (!resp.status) {
				console.error('Failed to save issue', resp.msg);
				return;
			}
			setShowIssueModal(false);
			setNewIssue('');
			setNewDescription('');
			setNewSeverity(1);
			setNewStartedAt(new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,16));
			setNewResolvedAt('');
			setEditingType(null);
			setEditingId(null);
			fetchRows();
		} catch (err) {
			console.error('Save issue error', err);
		}
	};

	return (
		<AppLayout>
			<div className="wr-cal-container">
				<div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
					<h2 style={{ margin:0 }}>Health Info</h2>
					<div style={{ display:'flex', gap:8 }}>
						<button onClick={() => {
							// Prefill Taken At based on selected day at 09:00 local
							const pad = (n) => String(n).padStart(2,'0');
							const makeLocal = (d, h, m) => {
								const y = d.getFullYear();
								const mo = pad(d.getMonth()+1);
								const da = pad(d.getDate());
								const hh = pad(h);
								const mm = pad(m);
								return `${y}-${mo}-${da}T${hh}:${mm}`;
							};
							const base = selectedDate || new Date();
							setNewTakenAt(makeLocal(base, 9, 0));
							setShowModal(true);
						}} style={{ padding:'8px 12px', fontSize:14, cursor:'pointer', background:'#e6f0ff', color:'#0a58ca', border:'1px solid #bcd0ff', borderRadius:6 }}>+ Add Med</button>
						<button onClick={() => {
							// Prefill doneAt with selected date
							const d = selectedDate || new Date();
							const y = d.getFullYear();
							const m = String(d.getMonth()+1).padStart(2,'0');
							const da = String(d.getDate()).padStart(2,'0');
							setExDoneAt(`${y}-${m}-${da}`);
							setExName('cardio');
							setExDescription('');
							setShowExerciseModal(true);
						}} style={{ padding:'8px 12px', fontSize:14, cursor:'pointer', background:'#e6ffe6', color:'#0f5132', border:'1px solid #b6e1b6', borderRadius:6 }}>+ Add Exercise</button>
						<button onClick={() => {
							// Prefill createdAt with selected date
							const d = selectedDate || new Date();
							const y = d.getFullYear();
							const m = String(d.getMonth()+1).padStart(2,'0');
							const da = String(d.getDate()).padStart(2,'0');
							setBpCreatedAt(`${y}-${m}-${da}`);
							setBpSystolic('');
							setBpDiastolic('');
							setShowBloodPressureModal(true);
						}} style={{ padding:'8px 12px', fontSize:14, cursor:'pointer', background:'#e5e5e5', color:'#333', border:'1px solid #b3b3b3', borderRadius:6 }}>+ Add Blood Pressure</button>
						<button onClick={() => {
							// Prefill nightOf with selected date; preload existing record if present
							const d = selectedDate || new Date();
							const y = d.getFullYear();
							const m = String(d.getMonth()+1).padStart(2,'0');
							const da = String(d.getDate()).padStart(2,'0');
							const key = `${y}-${m}-${da}`;
							setSlNightOf(key);
							const existing = sleepByDate[key];
							if (existing) {
								setSlQuality(existing.quality || 8);
								setSlMagnesium(!!existing.magnesiumTaken);
								setSlNotes(existing.notes || '');
							} else {
								setSlQuality(8);
								setSlMagnesium(false);
								setSlNotes('');
							}
							setShowSleepModal(true);
						}} style={{ padding:'8px 12px', fontSize:14, cursor:'pointer', background:'#f3e8ff', color:'#7e22ce', border:'1px solid #e9d5ff', borderRadius:6 }}>+ Add Sleep</button>
						<button onClick={async () => {
							// Prefill dates based on selected day
							const pad = (n) => String(n).padStart(2,'0');
							const makeLocal = (d, h, m) => {
								const y = d.getFullYear();
								const mo = pad(d.getMonth()+1);
								const da = pad(d.getDate());
								const hh = pad(h);
								const mm = pad(m);
								return `${y}-${mo}-${da}T${hh}:${mm}`;
							};
							const base = selectedDate || new Date();
							setNewStartedAt(makeLocal(base, 9, 0));
							setNewResolvedAt(makeLocal(base, 22, 0));
							// Load prior issues for select
							try {
								const resp = await healthApi.getIssues({ startDate: '1970-01-01', endDate: '2100-12-31' });
								const names = (resp && resp.status && resp.data && Array.isArray(resp.data.rows))
									? Array.from(new Set(resp.data.rows.map(r => r.issue || r.name).filter(Boolean)))
									: [];
								setIssueOptions(names.sort());
								setSelectedIssue(names[0] || '');
								setIsNewIssue(false);
								setNewIssue('');
							} catch (e) {
								console.error('Failed to load prior issues', e);
								setIssueOptions([]);
								setSelectedIssue('');
							}
							setShowIssueModal(true);
						}} style={{ padding:'8px 12px', fontSize:14, cursor:'pointer', background:'#ffe6e6', color:'#c1121f', border:'1px solid #ffb3b3', borderRadius:6 }}>+ Add Issue</button>
					</div>
				</div>

		{!isMobile && (Object.keys(countsByName).length > 0 || Object.keys(issueCountsByName).length > 0) && (
					<div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))', gap:12, margin:'8px 0 16px 0' }}>
						{Object.keys(countsByName).length > 0 && (
							<div style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:12, background:'#f8fafc' }}>
								<div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
									<h3 style={{ margin:0, fontSize:16 }}>Med Summary</h3>
								</div>
								<div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
									{Object.entries(countsByName).sort((a,b)=>b[1]-a[1]).map(([name, count]) => (
										<div key={name} style={{ padding:'6px 10px', borderRadius:999, background:'#e6f0ff', color:'#0a58ca', fontSize:12 }}>
											{name}: {count}
										</div>
									))}
								</div>
							</div>
						)}
					{Object.keys(issueCountsByName).length > 0 && (
							<div style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:12, background:'#fff7f7' }}>
								<div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
									<h3 style={{ margin:0, fontSize:16 }}>Issue Summary</h3>
								</div>
								<div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
									{Object.entries(issueCountsByName).sort((a,b)=>b[1]-a[1]).map(([name, count]) => (
										<div key={name} style={{ padding:'6px 10px', borderRadius:999, background:'#ffe6e6', color:'#c1121f', fontSize:12 }}>
											{name}: {count}
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				)}
			{!isMobile && (
				<Calendar
					activeStartDate={activeMonth}
					onActiveStartDateChange={onActiveStartDateChange}
					value={selectedDate}
					onChange={onSelectDate}
					view="month"
					locale="en-US"
					tileContent={tileContent}
					prev2Label={null}
					next2Label={null}
					tileClassName={() => 'wr-cal-tile'}
				/>
			)}

			{isMobile && (
				<div className="wr-mobile-day">
					<div className="wr-mobile-day-header">
						<button className="wr-nav-btn" onClick={goPrevDay} aria-label="Previous day">‹</button>
						<div className="wr-mobile-day-title">
							{selectedDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
						</div>
						<div style={{ display:'flex', alignItems:'center', gap:8 }}>
						{(() => { const key = toLocalYMD(selectedDate); const hasAny = (issuesByDate[key]||[]).length>0 || (exercisesByDate[key]||[]).length>0 || (bloodPressureByDate[key]||[]).length>0; return hasAny ? (<button className="wr-day-edit-btn" onClick={() => setShowEditChooser(true)} title="Edit issue or exercise">✏️</button>) : null; })()}
							<button className="wr-nav-btn" onClick={goNextDay} aria-label="Next day">›</button>
						</div>
					</div>
					{(() => {
						const key = toLocalYMD(selectedDate);
						const medsText = medsTextByDate[key];
						const issuesForDay = issuesByDate[key] || [];
						const exForDay = exercisesByDate[key] || [];
						const bpForDay = bloodPressureByDate[key] || [];
						const sleepForDay = sleepByDate[key] || null;
						return (
							<div className="wr-mobile-day-body">
								{sleepForDay && (
									<div className="wr-mobile-section">
										<div className="wr-mobile-section-title">Sleep</div>
										<div style={{ display:'flex', alignItems:'center', gap:8 }}>
											<SleepMoon
												color={colorForSleepQuality(sleepForDay.quality)}
												title={`Sleep quality: ${sleepForDay.quality}/10 (tap to edit)`}
												onClick={() => openSleepModal(key)}
											/>
											<span>Quality {sleepForDay.quality}/10{sleepForDay.magnesiumTaken ? ' • magnesium taken' : ''}</span>
											<button className="wr-btn wr-btn-small" style={{ marginLeft:'auto' }} onClick={() => openSleepModal(key)}>Edit</button>
										</div>
										{sleepForDay.notes && (
											<pre className="wr-mobile-pre" style={{ marginTop:6 }}>{sleepForDay.notes}</pre>
										)}
									</div>
								)}
								{exForDay.length > 0 && (
									<div className="wr-mobile-section">
										<div className="wr-mobile-section-title">Exercises</div>
										<div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
											{exForDay.map((ex, idx) => {
												let icon = null;
												if (ex.name === 'cardio') icon = '❤️';
												else if (ex.name === 'upper_body') icon = '💪';
												else if (ex.name === 'tennis') icon = '🎾';
								return (
									<span
										key={`ex-${idx}`}
										className="wr-day-icon"
										title={ex.description || ex.name}
										onClick={() => copyToClipboard(ex.description || ex.name)}
										role="button"
										aria-label={`Copy exercise ${ex.name} description`}
									>
										{icon || '🏋️'}
									</span>
								);
											})}
										</div>
									</div>
								)}
								{bpForDay.length > 0 && (
									<div className="wr-mobile-section">
										<div className="wr-mobile-section-title">Blood Pressure</div>
										<div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
											{bpForDay.map((bp, idx) => {
												const tip = `Blood Pressure: ${bp.systolic}/${bp.diastolic} mmHg`;
												return (
													<span
														key={`bp-${idx}`}
														className="wr-day-icon"
														title={tip}
														onClick={() => copyToClipboard(tip)}
														role="button"
														aria-label={`Copy blood pressure ${bp.systolic}/${bp.diastolic}`}
													>
														🩺
													</span>
												);
											})}
										</div>
									</div>
								)}
								{medsText && (
									<div className="wr-mobile-section">
										<div className="wr-mobile-section-title">Medications</div>
										<pre className="wr-mobile-pre" style={{ color:'blue' }}>{medsText}</pre>
									</div>
								)}
								{issuesForDay.length > 0 && (
									<div className="wr-mobile-section">
										<div className="wr-mobile-section-title">Issues</div>
										<div style={{ display:'grid', gap:6 }}>
											{issuesForDay.map((iss, idx) => {
												const sev = iss.severity;
												const color = colorForIssueSeverity(sev);
												return (
													<div key={idx} style={{ color, fontWeight: sev != null && sev >= 8 ? 700 : 500 }} title={iss.description || iss.label}>
														{iss.label}
														{sev != null && (
															<span style={{ fontWeight:700, opacity:0.85 }}> ·{sev}</span>
														)}
													</div>
												);
											})}
										</div>
									</div>
								)}
							</div>
						);
					})()}
				</div>
			)}
			{loading && <div style={{ marginTop:8, fontSize:12, color:'#666' }}>Loading…</div>}
			</div>

			{showEditChooser && (() => {
				const key = toLocalYMD(selectedDate);
				const dayIssues = rawIssuesByDate[key] || [];
				const dayExercises = rawExercisesByDate[key] || [];
				const dayBloodPressure = rawBloodPressureByDate[key] || [];
				return (
					<div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex: 1200 }}>
						<div style={{ background:'#fff', padding:16, minWidth:320, borderRadius:6 }}>
							<h3 style={{ marginTop:0 }}>Edit issue or exercise</h3>
							<div style={{ display:'grid', gap:8 }}>
								{dayExercises.map((ex) => (
									<button key={`ex-${ex.id || ex.doneAt || ex.name}`} onClick={() => {
										setEditingType('exercise');
										setEditingId(ex.id);
										setExName((ex.name || '').toLowerCase());
										const d = toDateOnly(ex.doneAt || ex.done_at);
										setExDoneAt(d ? toLocalYMD(d) : toLocalYMD(selectedDate));
										setExDescription(ex.description || '');
										setShowEditChooser(false);
										setShowExerciseModal(true);
									}} style={{ padding:'8px 12px', background:'#000', color:'#fff', borderRadius:6, textAlign:'left' }}>
										Edit exercise: {ex.name}
									</button>
								))}
								{dayIssues.map((iss) => (
									<button key={`iss-${iss.id || iss.startedAt || iss.issue}`} onClick={() => {
										setEditingType('issue');
										setEditingId(iss.id);
										setIsNewIssue(false);
										setSelectedIssue(iss.issue || iss.name || '');
										setNewIssue('');
										setNewDescription(iss.description || '');
										setNewSeverity(Number(iss.severity) || 1);
										const s = toDateFromAny(iss.startedAt || iss.started_at);
										const r = toDateFromAny(iss.resolvedAt || iss.resolved_at);
										const fmt = (d) => d ? new Date(d.getTime()-d.getTimezoneOffset()*60000).toISOString().slice(0,16) : '';
										setNewStartedAt(fmt(s) || new Date(Date.now()-new Date().getTimezoneOffset()*60000).toISOString().slice(0,16));
										setNewResolvedAt(fmt(r));
										setShowEditChooser(false);
										setShowIssueModal(true);
									}} style={{ padding:'8px 12px', background:'#ffa500', color:'#000', borderRadius:6, textAlign:'left' }}>
										Edit issue: {iss.issue || iss.name}
									</button>
								))}
							</div>
							<div style={{ display:'flex', justifyContent:'flex-end', marginTop:12 }}>
								<button onClick={() => setShowEditChooser(false)}>Close</button>
							</div>
						</div>
					</div>
				);
			})()}

			{showModal && (
				<div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex: 1000 }}>
					<div style={{ background:'#fff', padding:16, minWidth:320, borderRadius:6 }}>
						<h3 style={{ marginTop:0, color:'#0a58ca' }}>Add Medication</h3>
						<form onSubmit={submitNewMedication}>
							<div style={{ marginBottom:10 }}>
								<label>Name</label>
								<input value={newName} onChange={(e)=>setNewName(e.target.value)} required style={{ width:'100%' }} />
							</div>
							<div style={{ marginBottom:10 }}>
								<label>Taken At</label>
								<input type="datetime-local" value={newTakenAt} onChange={(e)=>setNewTakenAt(e.target.value)} required style={{ width:'100%' }} />
							</div>
							<div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
								<button type="button" onClick={()=>setShowModal(false)}>Cancel</button>
								<button type="submit" style={{ background:'#e6f0ff', color:'#0a58ca', border:'1px solid #bcd0ff' }}>Save</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{showExerciseModal && (
				<div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex: 1000 }}>
					<div style={{ background:'#fff', padding:16, minWidth:360, borderRadius:6 }}>
						<h3 style={{ marginTop:0, color:'#0f5132' }}>Add Exercise</h3>
						<form onSubmit={async (e) => {
							e.preventDefault();
							try {
							const resp = editingType === 'exercise' && editingId
								? await healthApi.updateExercise({ id: editingId, name: exName, doneAt: exDoneAt, description: exDescription })
								: await healthApi.createExercise({ name: exName, doneAt: exDoneAt, description: exDescription });
								if (!resp.status) {
								console.error('Failed to save exercise', resp.msg);
									return;
								}
								setShowExerciseModal(false);
								setExName('cardio');
								setExDescription('');
							setEditingType(null);
							setEditingId(null);
								// Reset date to selected date
								const d = selectedDate || new Date();
								const y = d.getFullYear();
								const m = String(d.getMonth()+1).padStart(2,'0');
								const da = String(d.getDate()).padStart(2,'0');
								setExDoneAt(`${y}-${m}-${da}`);
								fetchRows();
							} catch (err) {
							console.error('Save exercise error', err);
							}
						}}>
							<div style={{ marginBottom:10 }}>
								<label>Name</label>
								<select value={exName} onChange={(e)=>setExName(e.target.value)} required style={{ width:'100%' }}>
									<option value="cardio">cardio</option>
									<option value="upper_body">upper_body</option>
									<option value="tennis">tennis</option>
								</select>
							</div>
							<div style={{ marginBottom:10 }}>
								<label>Done At</label>
								<input type="date" value={exDoneAt} onChange={(e)=>setExDoneAt(e.target.value)} required style={{ width:'100%' }} />
							</div>
							<div style={{ marginBottom:10 }}>
								<label>Description</label>
								<textarea value={exDescription} onChange={(e)=>setExDescription(e.target.value)} rows={3} style={{ width:'100%' }} />
							</div>
							<div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
								<button type="button" onClick={()=>setShowExerciseModal(false)}>Cancel</button>
								<button type="submit" style={{ background:'#e6ffe6', color:'#0f5132', border:'1px solid #b6e1b6' }}>Save</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{showIssueModal && (
				<div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex: 1000 }}>
					<div style={{ background:'#fff', padding:16, minWidth:360, borderRadius:6 }}>
						<h3 style={{ marginTop:0, color:'#c1121f' }}>{editingType === 'issue' ? 'Edit Issue' : 'Add Issue'}</h3>
						<form onSubmit={submitNewIssue}>
						<div style={{ marginBottom:10 }}>
							<div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
								<label>Issue</label>
								{editingType !== 'issue' && (
									<label style={{ display:'flex', alignItems:'center', gap:6 }}>
										<input type="checkbox" checked={isNewIssue} onChange={(e)=>setIsNewIssue(e.target.checked)} /> New issue
									</label>
								)}
							</div>
							{editingType === 'issue' ? (
								<input value={selectedIssue} disabled style={{ width:'100%', background:'#f5f5f5' }} />
							) : (
								!isNewIssue ? (
									<select value={selectedIssue} onChange={(e)=>setSelectedIssue(e.target.value)} required style={{ width:'100%' }}>
										<option value="" disabled>Select an existing issue</option>
										{issueOptions.map(name => (
											<option key={name} value={name}>{name}</option>
										))}
									</select>
								) : (
									<input value={newIssue} onChange={(e)=>setNewIssue(e.target.value)} placeholder="Enter new issue" required style={{ width:'100%' }} />
								)
							)}
						</div>
							<div style={{ marginBottom:10 }}>
								<label>Description</label>
								<textarea value={newDescription} onChange={(e)=>setNewDescription(e.target.value)} rows={3} style={{ width:'100%' }} />
							</div>
							<div style={{ marginBottom:10 }}>
								<label>Severity (1-10)</label>
								<input type="number" min={1} max={10} value={newSeverity} onChange={(e)=>setNewSeverity(e.target.value)} required style={{ width:120 }} />
							</div>
							<div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
								<div>
									<label>Started At</label>
									<input type="datetime-local" value={newStartedAt} onChange={(e)=>setNewStartedAt(e.target.value)} required style={{ width:'100%' }} />
								</div>
								<div>
									<label>Resolved At (optional)</label>
									<input type="datetime-local" value={newResolvedAt} onChange={(e)=>setNewResolvedAt(e.target.value)} style={{ width:'100%' }} />
								</div>
							</div>
							<div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
							<button type="button" onClick={()=>{ setShowIssueModal(false); setEditingType(null); setEditingId(null); }}>Cancel</button>
							<button type="submit" style={{ background:'#ffe6e6', color:'#c1121f', border:'1px solid #ffb3b3' }}>Save</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{showBloodPressureModal && (
				<div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex: 1000 }}>
					<div style={{ background:'#fff', padding:16, minWidth:360, borderRadius:6 }}>
						<h3 style={{ marginTop:0, color:'#666' }}>Add Blood Pressure</h3>
						<form onSubmit={async (e) => {
							e.preventDefault();
							try {
								const resp = await healthApi.createBloodPressure({ systolic: Number(bpSystolic), diastolic: Number(bpDiastolic), createdAt: bpCreatedAt });
								if (!resp.status) {
									console.error('Failed to save blood pressure', resp.msg);
									return;
								}
								setShowBloodPressureModal(false);
								setBpSystolic('');
								setBpDiastolic('');
								// Reset date to selected date
								const d = selectedDate || new Date();
								const y = d.getFullYear();
								const m = String(d.getMonth()+1).padStart(2,'0');
								const da = String(d.getDate()).padStart(2,'0');
								setBpCreatedAt(`${y}-${m}-${da}`);
								fetchRows();
							} catch (err) {
								console.error('Save blood pressure error', err);
							}
						}}>
							<div style={{ marginBottom:10 }}>
								<label>Systolic (mmHg)</label>
								<input type="number" value={bpSystolic} onChange={(e)=>setBpSystolic(e.target.value)} required style={{ width:'100%' }} />
							</div>
							<div style={{ marginBottom:10 }}>
								<label>Diastolic (mmHg)</label>
								<input type="number" value={bpDiastolic} onChange={(e)=>setBpDiastolic(e.target.value)} required style={{ width:'100%' }} />
							</div>
							<div style={{ marginBottom:10 }}>
								<label>Date</label>
								<input type="date" value={bpCreatedAt} onChange={(e)=>setBpCreatedAt(e.target.value)} required style={{ width:'100%' }} />
							</div>
							<div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
								<button type="button" onClick={()=>setShowBloodPressureModal(false)}>Cancel</button>
								<button type="submit" style={{ background:'#e5e5e5', color:'#333', border:'1px solid #b3b3b3' }}>Save</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{showSleepModal && (
				<div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex: 1000 }}>
					<div style={{ background:'#fff', padding:16, minWidth:360, borderRadius:6 }}>
						<h3 style={{ marginTop:0, color:'#7e22ce' }}>{sleepByDate[slNightOf] ? 'Edit Sleep' : 'Add Sleep'}</h3>
						<form onSubmit={async (e) => {
							e.preventDefault();
							try {
								const resp = await healthApi.createSleep({
									nightOf: slNightOf,
									quality: Number(slQuality),
									magnesiumTaken: slMagnesium,
									notes: slNotes,
								});
								if (!resp.status) {
									console.error('Failed to save sleep', resp.msg);
									return;
								}
								setShowSleepModal(false);
								// Reset to selected date defaults
								const d = selectedDate || new Date();
								const y = d.getFullYear();
								const m = String(d.getMonth()+1).padStart(2,'0');
								const da = String(d.getDate()).padStart(2,'0');
								setSlNightOf(`${y}-${m}-${da}`);
								setSlQuality(8);
								setSlMagnesium(false);
								setSlNotes('');
								fetchRows();
							} catch (err) {
								console.error('Save sleep error', err);
							}
						}}>
							<div style={{ marginBottom:10 }}>
								<label>Night Of</label>
								<input type="date" value={slNightOf} onChange={(e)=>setSlNightOf(e.target.value)} required style={{ width:'100%' }} />
							</div>
							<div style={{ marginBottom:10 }}>
								<label>Quality: {slQuality}/10 (1 = worst, 10 = best)</label>
								<div style={{ display:'flex', alignItems:'center', gap:10 }}>
									<input type="range" min={1} max={10} step={1} value={slQuality} onChange={(e)=>setSlQuality(Number(e.target.value))} style={{ flex:1 }} />
									<SleepMoon color={colorForSleepQuality(slQuality)} title={`Quality ${slQuality}/10`} />
								</div>
							</div>
							<div style={{ marginBottom:10 }}>
								<label style={{ display:'flex', alignItems:'center', gap:8 }}>
									<input type="checkbox" checked={slMagnesium} onChange={(e)=>setSlMagnesium(e.target.checked)} /> Magnesium taken
								</label>
							</div>
							<div style={{ marginBottom:10 }}>
								<label>Notes</label>
								<textarea value={slNotes} onChange={(e)=>setSlNotes(e.target.value)} rows={3} style={{ width:'100%' }} />
							</div>
							<div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
								<button type="button" onClick={()=>setShowSleepModal(false)}>Cancel</button>
								<button type="submit" style={{ background:'#f3e8ff', color:'#7e22ce', border:'1px solid #e9d5ff' }}>Save</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</AppLayout>
	);
};

export { ViewHealthScreen };
